import { CustomButton } from '@ui-kit'
import {
  clearForceMsalPromptNextLogin,
  setForceMsalPromptNextLogin,
  getResolvedAuthProvider,
} from '@app-store'
import { allowMsalShellForThisTabAfterLogoutLogin } from '@ecrm-msal'
import { subscribeCrossTabLoginSuccess } from '@ecrm-session'
import { AUTH_PROVIDER, NAMESPACES } from '../utils/constants'
import { environment } from '../utils/environment'
import { LOGOUT_PAGE_STRINGS } from '../utils/strings'
import {
  persist,
  RootState,
  useAppDispatch,
  useAppSelector,
} from '../utils/agent.store'
import { useCallback, useEffect, useRef } from 'react'
import {
  clearSessionPostLogoutReturnPath,
  getSessionPostLogoutReturnPath,
  isSafeReturnPath,
  setSessionPostLogoutReturnPath,
} from '../utils/postLogoutReturnPath'

const FORCE_MSAL_PROMPT_QUERY = 'forceMsalPromptNextLogin'
const RETURN_PATH_QUERY = 'returnPath'

const LogoutPage: React.FC = () => {
  const dispatch = useAppDispatch()
  const redirectedRef = useRef(false)
  const authProfileLoggedOut = useAppSelector(
    (state: RootState) => state.authUser.isAuthenticated === false
  )
  const sessionHasBackendAuth = useAppSelector((state: RootState) =>
    Boolean(state.session.isAuthenticated && state.session.token)
  )

  const navigateToResume = useCallback(() => {
    if (redirectedRef.current) return
    redirectedRef.current = true
    const path = getSessionPostLogoutReturnPath()
    clearSessionPostLogoutReturnPath()
    dispatch(clearForceMsalPromptNextLogin())
    const origin = window.location.origin
    window.location.href =
      path && isSafeReturnPath(path) ? `${origin}${path}` : origin
  }, [dispatch])

  const handleLoginClick = () => {
    allowMsalShellForThisTabAfterLogoutLogin()
    // Resume the page this tab was on when it logged out, not the app root.
    // Logout stores that path per-tab; this button used to navigate to the bare
    // origin and discard it, so every re-login landed on the dashboard. Landing
    // on the real path also makes the boot saga pass IT as `returnPath` to
    // /login, so the Microsoft round trip comes back to the right place too.
    const path = getSessionPostLogoutReturnPath()
    clearSessionPostLogoutReturnPath()
    const origin = window.location.origin
    window.location.href =
      path && isSafeReturnPath(path) ? `${origin}${path}` : origin
  }

  /** Hydrate Redux + session from URL when session expiry wiped persist (query from handleSessionExpiry). */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const rp = params.get(RETURN_PATH_QUERY)
    if (rp && isSafeReturnPath(rp)) {
      setSessionPostLogoutReturnPath(rp)
    }
    if (params.get(FORCE_MSAL_PROMPT_QUERY) === '1') {
      dispatch(setForceMsalPromptNextLogin(true))
    }
    if (params.toString()) {
      void persist.flush().then(() => {
        const clean = new URL(window.location.href)
        clean.searchParams.delete(FORCE_MSAL_PROMPT_QUERY)
        clean.searchParams.delete(RETURN_PATH_QUERY)
        window.history.replaceState(
          {},
          '',
          clean.pathname + clean.search + clean.hash
        )
      })
    }
  }, [dispatch])

  useEffect(() => {
    if (authProfileLoggedOut) return
    if (!sessionHasBackendAuth) return
    navigateToResume()
  }, [authProfileLoggedOut, sessionHasBackendAuth, navigateToResume])

  /**
   * Resume this tab when a sibling tab signs in — the "logged in on one tab but
   * the others stayed on the logout screen" case.
   *
   * RouterWrapper already subscribes to the same broadcast, but its handler is
   * guarded on `authUser.isAuthenticated !== false`, and logout destroys exactly
   * that value: it clears the persisted authUser and then reloads the page, so
   * the slice re-initialises from initialState where isAuthenticated defaults to
   * TRUE. That guard can therefore never pass here, in ANY provider, so the
   * broadcast arrives and is discarded.
   *
   * This is added as a separate MS_BACKEND-only subscriber rather than by
   * changing that guard: the MSAL flow is being retired in favour of the
   * backend-managed one, so its code path is left exactly as-is. Mirrors what
   * admin-desk's logout page already does.
   *
   * The provider gate also keeps the two handlers from ever both firing. They
   * both read the stored return path and the first one to run CLEARS it, so the
   * second would navigate to the app root instead — sending the user to the
   * dashboard and looking like a fresh regression. `navigateToResume` has its
   * own single-shot guard for the same reason.
   */
  useEffect(() => {
    const provider = getResolvedAuthProvider() ?? environment.authProvider
    if (provider !== AUTH_PROVIDER.MS_BACKEND) return
    return subscribeCrossTabLoginSuccess(() => {
      navigateToResume()
    })
  }, [navigateToResume])

  return (
    <div className="flex items-center justify-center min-h-screen bg-accent-100">
      <div className="bg-accent-0 rounded-lg shadow-md p-6 max-w-md text-center">
        <h1 className="text-base font-medium text-accent-700">
          {LOGOUT_PAGE_STRINGS.TITLE}
        </h1>
        <p className="mt-4 text-accent-600 font-light text-xs">
          {LOGOUT_PAGE_STRINGS.MESSAGE}
        </p>
        <p className="mt-2 text-accent-500 font-light text-xs">
          {LOGOUT_PAGE_STRINGS.SUBTEXT}
        </p>
        <div className="pt-4">
          <CustomButton
            type="button"
            isFilled={true}
            horizontalPaddingClasses="px-4.5"
            verticalPaddingClasses="py-2"
            onClick={handleLoginClick}
            dataQaId={'login-button-logout-screen'}
          >
            {NAMESPACES.LOGIN_BUTTON}
          </CustomButton>
        </div>
      </div>
    </div>
  )
}

export default LogoutPage
